import { ref, shallowRef, type Ref } from 'vue';

import type {
  ApiRes,
  GetAgentHistoryIndexResponse,
  GetSessionMessagesResponse,
} from '@/practice/api';
import {
  createBrowserHistoryCacheBroadcast,
  type HistoryCacheBroadcastAdapter,
} from '@/practice/historyCacheBroadcast';
import { randomUuid } from '@/practice/crypto';
import type { HistorySessionSummary, Message } from '@/types/model';

export type HistoryIndexSnapshot = {
  readonly sessions: readonly HistorySessionSummary[];
  readonly etag: string;
};

export type HistoryMessagesSnapshot = {
  readonly messages: readonly Message[];
  readonly etag: string;
};

export interface HistoryApi {
  getAgentHistoryIndex(
    result: ApiRes<GetAgentHistoryIndexResponse>,
    agentId: string,
    ifNoneMatch: string | null,
  ): Promise<void>;
  getSessionMessages(
    result: ApiRes<GetSessionMessagesResponse>,
    agentId: string,
    sessionId: string,
    ifNoneMatch: string | null,
  ): Promise<void>;
}

type HistoryRequestState<T> = {
  snapshot: Ref<T | null>;
  isLoading: Ref<boolean>;
  isRevalidating: Ref<boolean>;
  error: Ref<number | null>;
  generation: number;
};

type HistoryAgentCacheEntry = {
  index: HistoryRequestState<HistoryIndexSnapshot>;
  messages: Map<string, HistoryRequestState<HistoryMessagesSnapshot>>;
};

const createRequestState = <T>(): HistoryRequestState<T> => ({
  snapshot: shallowRef<T | null>(null),
  isLoading: ref(false),
  isRevalidating: ref(false),
  error: ref<number | null>(null),
  generation: 0,
});

const resetRequestState = <T>(state: HistoryRequestState<T>): void => {
  state.generation += 1;
  state.snapshot.value = null;
  state.isLoading.value = false;
  state.isRevalidating.value = false;
  state.error.value = null;
};

const cloneMessages = (messages: readonly Message[]): Message[] => {
  return messages.map(message => ({
    ...message,
    content: message.content.map(section => ({ ...section })),
  }));
};

export const historyCacheKey = (userId: string, agentId: string): string => {
  return `${encodeURIComponent(userId)}::${encodeURIComponent(agentId)}`;
};

export const latestHistorySessionId = (
  sessions: readonly HistorySessionSummary[],
): string | null => {
  if (sessions.length === 0) {
    return null;
  }
  return sessions[sessions.length - 1].sessionId;
};

export const resolveHistorySessionId = (
  sessions: readonly HistorySessionSummary[],
  selectedSessionId: string | null,
  requestedSessionId: string | null,
  selectLastByDefault: boolean,
): string | null => {
  if (requestedSessionId !== null) {
    return sessions.some(session => session.sessionId === requestedSessionId)
      ? requestedSessionId
      : null;
  }
  if (
    selectedSessionId !== null
    && sessions.some(session => session.sessionId === selectedSessionId)
  ) {
    return selectedSessionId;
  }
  if (selectLastByDefault && sessions.length > 0) {
    return sessions[sessions.length - 1].sessionId;
  }
  return null;
};

export const resolveHistorySessionIdAfterRevalidation = (
  loadedSessions: readonly HistorySessionSummary[],
  selectedSessionId: string | null,
  requestedSessionId: string | null,
  selectLastByDefault: boolean,
  cachedLastSessionId: string | null,
): string | null => {
  const loadedLastSessionId = latestHistorySessionId(loadedSessions);
  if (
    requestedSessionId === null
    && selectLastByDefault
    && loadedLastSessionId !== null
    && loadedLastSessionId !== cachedLastSessionId
  ) {
    return loadedLastSessionId;
  }
  return resolveHistorySessionId(
    loadedSessions,
    selectedSessionId,
    requestedSessionId,
    selectLastByDefault,
  );
};

export class HistoryCacheStore {
  private readonly entries = new Map<string, HistoryAgentCacheEntry>();
  private readonly indexRequests = new Map<string, Promise<void>>();
  private readonly messageRequests = new Map<string, Promise<void>>();
  private readonly revision = ref(0);
  private readonly sourceId = randomUuid();
  private readonly unsubscribeBroadcast: (() => void) | null;
  private clearGeneration = 0;

  constructor(
    private readonly maxMessageSessionsPerAgent: number,
    private readonly broadcast: HistoryCacheBroadcastAdapter | null = null,
  ) {
    if (maxMessageSessionsPerAgent < 1) {
      throw new Error('History message cache size must be positive');
    }
    this.unsubscribeBroadcast = this.broadcast?.subscribe(message => {
      if (message.sourceId === this.sourceId) {
        return;
      }
      if (message.type === 'clear') {
        this.clearInternal(false);
        return;
      }
      this.updateSessionTitleInternal(
        message.userId,
        message.agentId,
        message.sessionId,
        message.title,
        false,
      );
    }) ?? null;
  }

  private getAgentEntry(userId: string, agentId: string): HistoryAgentCacheEntry {
    const cacheKey = historyCacheKey(userId, agentId);
    const existing = this.entries.get(cacheKey);
    if (existing !== undefined) {
      return existing;
    }
    const entry: HistoryAgentCacheEntry = {
      index: createRequestState<HistoryIndexSnapshot>(),
      messages: new Map<string, HistoryRequestState<HistoryMessagesSnapshot>>(),
    };
    this.entries.set(cacheKey, entry);
    this.revision.value += 1;
    return entry;
  }

  private touchMessageEntry(
    agentEntry: HistoryAgentCacheEntry,
    sessionId: string,
    state: HistoryRequestState<HistoryMessagesSnapshot>,
  ): void {
    agentEntry.messages.delete(sessionId);
    agentEntry.messages.set(sessionId, state);
    while (agentEntry.messages.size > this.maxMessageSessionsPerAgent) {
      const oldestSessionId = agentEntry.messages.keys().next().value as string | undefined;
      if (oldestSessionId === undefined) {
        return;
      }
      const evicted = agentEntry.messages.get(oldestSessionId);
      agentEntry.messages.delete(oldestSessionId);
      if (evicted !== undefined) {
        resetRequestState(evicted);
      }
    }
  }

  indexFor(userId: string, agentId: string): HistoryIndexSnapshot | null {
    void this.revision.value;
    return this.entries.get(historyCacheKey(userId, agentId))?.index.snapshot.value ?? null;
  }

  updateSessionTitle(
    userId: string,
    agentId: string,
    sessionId: string,
    title: string,
  ): boolean {
    return this.updateSessionTitleInternal(
      userId,
      agentId,
      sessionId,
      title,
      true,
    );
  }

  private updateSessionTitleInternal(
    userId: string,
    agentId: string,
    sessionId: string,
    title: string,
    shouldBroadcast: boolean,
  ): boolean {
    const state = this.entries.get(historyCacheKey(userId, agentId))?.index;
    const snapshot = state?.snapshot.value;
    if (state === undefined || snapshot === null || snapshot === undefined) {
      return false;
    }
    let updated = false;
    const sessions = snapshot.sessions.map(session => {
      if (session.sessionId !== sessionId) {
        return session;
      }
      updated = true;
      return { ...session, title };
    });
    if (!updated) {
      return false;
    }
    state.snapshot.value = {
      sessions,
      etag: '',
    };
    this.revision.value += 1;
    if (shouldBroadcast) {
      this.broadcast?.publish({
        type: 'titleUpdated',
        sourceId: this.sourceId,
        userId,
        agentId,
        sessionId,
        title,
      });
    }
    return true;
  }

  messagesFor(
    userId: string,
    agentId: string,
    sessionId: string,
  ): HistoryMessagesSnapshot | null {
    void this.revision.value;
    const agentEntry = this.entries.get(historyCacheKey(userId, agentId));
    const state = agentEntry?.messages.get(sessionId);
    if (agentEntry === undefined || state === undefined) {
      return null;
    }
    this.touchMessageEntry(agentEntry, sessionId, state);
    return state.snapshot.value;
  }

  isIndexLoading(userId: string, agentId: string): boolean {
    void this.revision.value;
    return this.entries.get(historyCacheKey(userId, agentId))?.index.isLoading.value ?? false;
  }

  isIndexRevalidating(userId: string, agentId: string): boolean {
    void this.revision.value;
    return this.entries.get(historyCacheKey(userId, agentId))?.index.isRevalidating.value ?? false;
  }

  indexError(userId: string, agentId: string): number | null {
    void this.revision.value;
    return this.entries.get(historyCacheKey(userId, agentId))?.index.error.value ?? null;
  }

  areMessagesLoading(userId: string, agentId: string, sessionId: string): boolean {
    void this.revision.value;
    return this.entries
      .get(historyCacheKey(userId, agentId))
      ?.messages.get(sessionId)
      ?.isLoading.value ?? false;
  }

  areMessagesRevalidating(userId: string, agentId: string, sessionId: string): boolean {
    void this.revision.value;
    return this.entries
      .get(historyCacheKey(userId, agentId))
      ?.messages.get(sessionId)
      ?.isRevalidating.value ?? false;
  }

  messagesError(userId: string, agentId: string, sessionId: string): number | null {
    void this.revision.value;
    return this.entries
      .get(historyCacheKey(userId, agentId))
      ?.messages.get(sessionId)
      ?.error.value ?? null;
  }

  async loadIndex(userId: string, agentId: string, api: HistoryApi): Promise<void> {
    if (userId.length === 0 || agentId.length === 0) {
      return;
    }
    const cacheKey = historyCacheKey(userId, agentId);
    const existingRequest = this.indexRequests.get(cacheKey);
    if (existingRequest !== undefined) {
      await existingRequest;
      return;
    }

    const agentEntry = this.getAgentEntry(userId, agentId);
    const state = agentEntry.index;
    state.generation += 1;
    const requestGeneration = state.generation;
    const clearGeneration = this.clearGeneration;
    const ifNoneMatch = state.snapshot.value?.etag ?? null;
    state.error.value = null;
    state.isLoading.value = state.snapshot.value === null;
    state.isRevalidating.value = state.snapshot.value !== null;

    let request: Promise<void>;
    request = (async () => {
      const result: ApiRes<GetAgentHistoryIndexResponse> = {
        data: shallowRef<GetAgentHistoryIndexResponse | null>(null),
        isFetching: ref(false),
        error: ref<number | null>(null),
      };
      try {
        await api.getAgentHistoryIndex(result, agentId, ifNoneMatch);
      } catch {
        result.error.value = 0;
      }

      const isCurrent = clearGeneration === this.clearGeneration
        && state.generation === requestGeneration
        && this.entries.get(cacheKey) === agentEntry;
      if (!isCurrent) {
        return;
      }
      const response = result.data.value;
      if (
        result.error.value !== null
        || response === null
        || response.agentId !== agentId
      ) {
        state.error.value = result.error.value ?? 0;
        return;
      }
      if (response.unchanged) {
        const current = state.snapshot.value;
        if (current === null) {
          state.error.value = 0;
          return;
        }
        state.snapshot.value = {
          sessions: current.sessions,
          etag: response.etag,
        };
        return;
      }

      const sessions = response.sessions.map(session => ({ ...session }));
      state.snapshot.value = {
        sessions,
        etag: response.etag,
      };
      const validSessionIds = new Set(sessions.map(session => session.sessionId));
      for (const [sessionId, messageState] of agentEntry.messages) {
        if (validSessionIds.has(sessionId)) {
          continue;
        }
        resetRequestState(messageState);
        agentEntry.messages.delete(sessionId);
      }
    })().finally(() => {
      if (
        clearGeneration === this.clearGeneration
        && state.generation === requestGeneration
        && this.entries.get(cacheKey) === agentEntry
      ) {
        state.isLoading.value = false;
        state.isRevalidating.value = false;
      }
      if (this.indexRequests.get(cacheKey) === request) {
        this.indexRequests.delete(cacheKey);
      }
    });
    this.indexRequests.set(cacheKey, request);
    await request;
  }

  async loadMessages(
    userId: string,
    agentId: string,
    sessionId: string,
    api: HistoryApi,
  ): Promise<void> {
    if (userId.length === 0 || agentId.length === 0 || sessionId.length === 0) {
      return;
    }
    const cacheKey = historyCacheKey(userId, agentId);
    const requestKey = `${cacheKey}::${encodeURIComponent(sessionId)}`;
    const existingRequest = this.messageRequests.get(requestKey);
    if (existingRequest !== undefined) {
      await existingRequest;
      return;
    }

    const agentEntry = this.getAgentEntry(userId, agentId);
    let state = agentEntry.messages.get(sessionId);
    if (state === undefined) {
      state = createRequestState<HistoryMessagesSnapshot>();
      this.revision.value += 1;
    }
    this.touchMessageEntry(agentEntry, sessionId, state);
    state.generation += 1;
    const requestGeneration = state.generation;
    const clearGeneration = this.clearGeneration;
    const ifNoneMatch = state.snapshot.value?.etag ?? null;
    state.error.value = null;
    state.isLoading.value = state.snapshot.value === null;
    state.isRevalidating.value = state.snapshot.value !== null;

    let request: Promise<void>;
    request = (async () => {
      const result: ApiRes<GetSessionMessagesResponse> = {
        data: shallowRef<GetSessionMessagesResponse | null>(null),
        isFetching: ref(false),
        error: ref<number | null>(null),
      };
      try {
        await api.getSessionMessages(result, agentId, sessionId, ifNoneMatch);
      } catch {
        result.error.value = 0;
      }

      const isCurrent = clearGeneration === this.clearGeneration
        && state.generation === requestGeneration
        && this.entries.get(cacheKey) === agentEntry
        && agentEntry.messages.get(sessionId) === state;
      if (!isCurrent) {
        return;
      }
      const response = result.data.value;
      if (
        result.error.value !== null
        || response === null
        || response.sessionId !== sessionId
      ) {
        state.error.value = result.error.value ?? 0;
        return;
      }
      if (response.unchanged) {
        const current = state.snapshot.value;
        if (current === null) {
          state.error.value = 0;
          return;
        }
        state.snapshot.value = {
          messages: current.messages,
          etag: response.etag,
        };
        this.touchMessageEntry(agentEntry, sessionId, state);
        return;
      }
      state.snapshot.value = {
        messages: cloneMessages(response.messages),
        etag: response.etag,
      };
      this.touchMessageEntry(agentEntry, sessionId, state);
    })().finally(() => {
      if (
        clearGeneration === this.clearGeneration
        && state.generation === requestGeneration
        && this.entries.get(cacheKey) === agentEntry
        && agentEntry.messages.get(sessionId) === state
      ) {
        state.isLoading.value = false;
        state.isRevalidating.value = false;
      }
      if (this.messageRequests.get(requestKey) === request) {
        this.messageRequests.delete(requestKey);
      }
    });
    this.messageRequests.set(requestKey, request);
    await request;
  }

  seedLegacy(
    userId: string,
    agentId: string,
    sessionIds: readonly string[],
    sessions: readonly (readonly Message[])[],
  ): void {
    if (userId.length === 0 || agentId.length === 0) {
      return;
    }
    const agentEntry = this.getAgentEntry(userId, agentId);
    agentEntry.index.generation += 1;
    const summaries: HistorySessionSummary[] = [];
    for (let index = 0; index < sessionIds.length; index += 1) {
      const sessionId = sessionIds[index];
      const messages = sessions[index] ?? [];
      if (sessionId === undefined || messages.length === 0) {
        continue;
      }
      const firstMessageTime = messages[0]?.time ?? 0;
      const lastMessageTime = messages[messages.length - 1]?.time ?? firstMessageTime;
      summaries.push({
        sessionId,
        agentId,
        kickstartId: '',
        kickstartType: '',
        firstMessageTime,
        lastMessageTime,
        duration: lastMessageTime - firstMessageTime,
        messageCount: messages.length,
      });
      let messageState = agentEntry.messages.get(sessionId);
      if (messageState === undefined) {
        messageState = createRequestState<HistoryMessagesSnapshot>();
        this.revision.value += 1;
      }
      messageState.generation += 1;
      messageState.snapshot.value = {
        messages: cloneMessages(messages),
        etag: `legacy-${sessionId}-${messages.length}-${lastMessageTime}`,
      };
      messageState.isLoading.value = false;
      messageState.isRevalidating.value = false;
      messageState.error.value = null;
      this.touchMessageEntry(agentEntry, sessionId, messageState);
    }
    const validSessionIds = new Set(
      summaries.map(summary => summary.sessionId),
    );
    for (const [sessionId, messageState] of agentEntry.messages) {
      if (validSessionIds.has(sessionId)) {
        continue;
      }
      resetRequestState(messageState);
      agentEntry.messages.delete(sessionId);
    }
    agentEntry.index.snapshot.value = {
      sessions: summaries,
      etag:
        `legacy-${agentId}-${summaries.length}-`
        + `${summaries.length === 0 ? 0 : summaries[summaries.length - 1]!.lastMessageTime}`,
    };
    agentEntry.index.isLoading.value = false;
    agentEntry.index.isRevalidating.value = false;
    agentEntry.index.error.value = null;
  }

  async preloadLatest(userId: string, agentId: string, api: HistoryApi): Promise<void> {
    if (userId.length === 0 || agentId.length === 0) {
      return;
    }
    await this.loadIndex(userId, agentId, api);
    const sessions = this.indexFor(userId, agentId)?.sessions ?? [];
    const lastSessionId = resolveHistorySessionId(sessions, null, null, true);
    if (lastSessionId === null) {
      return;
    }
    await this.loadMessages(userId, agentId, lastSessionId, api);
  }

  private clearInternal(publish: boolean): void {
    this.clearGeneration += 1;
    for (const agentEntry of this.entries.values()) {
      resetRequestState(agentEntry.index);
      for (const state of agentEntry.messages.values()) {
        resetRequestState(state);
      }
    }
    this.entries.clear();
    this.indexRequests.clear();
    this.messageRequests.clear();
    this.revision.value += 1;
    if (publish) {
      this.broadcast?.publish({
        type: 'clear',
        sourceId: this.sourceId,
      });
    }
  }

  clear(): void {
    this.clearInternal(true);
  }

  dispose(): void {
    this.unsubscribeBroadcast?.();
    this.broadcast?.close();
  }
}

export const historyCache = new HistoryCacheStore(
  20,
  createBrowserHistoryCacheBroadcast(),
);

export const preloadLatestHistory = async (
  userId: string,
  agentId: string,
  api: HistoryApi,
): Promise<void> => {
  await historyCache.preloadLatest(userId, agentId, api);
};

export const clearHistoryCache = (): void => {
  historyCache.clear();
};
